import {AsyncPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, computed, inject, input, OnDestroy, OnInit, output} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {MatDialog} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {BehaviorSubject, combineLatest, filter, map, Observable, Subscription, take} from 'rxjs';
import {
  GetChallengeBuilderProjectAction,
  StopChallengeAction,
} from 'src/app/core/store/challenge-builder/challenge-builder.action';
import {GetAllTrackersAction} from 'src/app/core/store/trackers/trackers.action';
import {OphBackButtonComponent} from 'src/app/shared/design/oph-back-button/oph-back-button.component';
import {Project} from '../../core/model/project';
import {User} from '../../core/model/user';
import {selectActiveUser} from '../../core/store/active-user/active-user.selector';
import {selectAllPrograms} from '../../core/store/programs/programs.selector';
import {
  DeleteProjectAction,
  DuplicateProjectAction,
  GetProjectsAction,
} from '../../core/store/projects/projects.action';
import {selectAllProjects, selectProjectsMap} from '../../core/store/projects/projects.selector';
import {GetAutoSkeds} from '../../core/store/schedule/schedule.action';
import {selectScheduleAutoSkeds} from '../../core/store/schedule/schedule.selector';
import {selectAllTeams, selectTeamsMap} from '../../core/store/teams/teams.selector';
import {selectAllUsers, selectUsersMap} from '../../core/store/users/users.selector';
import {Program} from '../../programs/shared/model/program';
import {AutoSked} from '../../schedule/shared/model/autosked';
import {OphAvatarMultiComponent} from '../../shared/design/oph-avatar-multi/oph-avatar-multi.component';
import {OphButtonModule} from '../../shared/design/oph-button/oph-button.module';
import {ChallengeWarningDialogComponent} from '../../shared/projects/challenges/challenge-warning-dialog/challenge-warning-dialog.component';
import {Team} from '../../teams/shared/model/team';
import {ProjectsDialogComponent} from '../dialogs/projects-dialog/projects-dialog.component';
import {ProjectsSkedSelectorDialogComponent} from '../dialogs/projects-sked-selector-dialog/projects-sked-selector-dialog.component';
import {ProjectsUsersTeamsSelectionDialogComponent} from '../dialogs/users-teams-selection-dialog/projects-users-teams-selection-dialog.component';
import {ProjectRowMenuComponent} from '../list/project-row-menu/project-row-menu.component';
import {ProjectSelectorMenuActionButtonComponent} from '../project-selector/action-button/project-selector-menu-action-button.component';
import {ProjectSelectorMenuProjectButtonComponent} from '../project-selector/project-button/project-selector-menu-project-button.component';
import {ProjectSelectorComponent} from '../project-selector/project-selector.component';
import {
  PROJECT_ACTION_OPTIONS,
  PROJECTS_ADD_SKED_DIALOG_CONFIG,
  PROJECTS_DIALOG_CONFIG,
  PROJECTS_USERS_TEAMS_SELECTION_DIALOG_CONFIG,
} from '../shared/projects-constants';
import {ProjectsSkedSelectorComponent} from '../sked-selector/projects-sked-selector.component';
import {MessageService} from './../../services/message.service';
import {MobileService} from 'src/app/core/page/mobile.service';

@Component({
  selector: 'projects-header',
  standalone: true,
  imports: [
    ProjectSelectorComponent,
    AsyncPipe,
    OphButtonModule,
    ProjectSelectorMenuActionButtonComponent,
    ProjectSelectorMenuProjectButtonComponent,
    ProjectsSkedSelectorComponent,
    MatIconModule,
    OphAvatarMultiComponent,
    OphBackButtonComponent,
    ProjectRowMenuComponent,
  ],
  templateUrl: './projects-header.component.html',
  styleUrl: './projects-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsHeaderComponent implements OnInit, OnDestroy {
  private readonly mobileService = inject(MobileService);
  subscriptions = new Subscription();
  readonly store = inject(Store);
  readonly MessageService = inject(MessageService);
  readonly router = inject(Router);
  readonly dialog = inject(MatDialog);

  project = input<Project>();
  /** When true, back button emits exitEdit instead of navigating to list. */
  isEditView = input<boolean>(false);
  projectSelected = output<Project>();
  editClick = output<void>();
  exitEdit = output<void>();

  /** Edit button label: "Stop & Edit" when active challenge, otherwise "Edit". */
  editButtonLabel = computed(() => {
    const p = this.project();
    return p?.status === 'active' && p?.isChallenge ? 'Stop & Edit' : 'Edit';
  });

  /** Selected project from parent input (as observable for template and pipes). */
  selectedProject$: Observable<Project | undefined> = toObservable(this.project);

  programs$: Observable<Program[]> = this.store.select(selectAllPrograms);
  projects$: Observable<Project[]> = this.store
    .select(selectAllProjects())
    .pipe(map(projects => projects.filter(project => !project.isChallenge)));
  challenges$: Observable<Project[]> = this.store
    .select(selectAllProjects(true))
    .pipe(map(projects => projects.filter(project => project.isChallenge)));
  projectsMap$: Observable<Record<string, Project>> = this.store.select(selectProjectsMap);
  autoSkeds$: Observable<AutoSked[]> = this.store.select(selectScheduleAutoSkeds);
  users$: Observable<User[]> = this.store.select(selectAllUsers);
  usersMap$: Observable<Record<string, User>> = this.store.select(selectUsersMap);
  teams$: Observable<Team[]> = this.store.select(selectAllTeams);
  teamsMap$: Observable<Record<string, Team>> = this.store.select(selectTeamsMap);
  activeUser$: Observable<User> = this.store.select(selectActiveUser);

  loading$ = new BehaviorSubject<Record<string, boolean>>({});

  projectSkeds$: Observable<string[]> = this.observeSelectedProjectAndSkeds();

  projectActionOptions = PROJECT_ACTION_OPTIONS;
  ngOnInit() {
    this.activeUser$
      .pipe(
        filter(u => !!u),
        take(1)
      )
      .subscribe(user => {
        this.store.dispatch(new GetAutoSkeds({params: {user: user._id}}));
      });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  observeSelectedProjectAndSkeds(): Observable<string[]> {
    return combineLatest([this.selectedProject$, this.autoSkeds$]).pipe(
      map(([project, autoSkeds]) => {
        if (!project || !autoSkeds) {
          return [];
        }
        const projectSkeds: string[] = [];
        autoSkeds.forEach(sked => {
          if (sked.projectIds?.length && sked.projectIds.includes(project._id)) {
            projectSkeds.push(sked.label);
          }
        });
        return projectSkeds;
      })
    );
  }

  onEditClick() {
    const project = this.project();
    if (!project) return;
    if (project.isChallenge && project.status === 'active') {
      this.dialog.open(ChallengeWarningDialogComponent, {
        autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
        backdropClass: 'oph-backdrop',
        panelClass: 'challenge-warning-dialog-panel',
        data: {
          type: 'challenge',
          action: 'stop',
          onConfirm: ({onSuccess, onFailure}) => {
            this.store.dispatch(
              new StopChallengeAction({
                projectId: project._id,
                onSuccess: () => {
                  this.store.dispatch(new GetChallengeBuilderProjectAction({projectId: project._id}));
                  this.store.dispatch(new GetProjectsAction({}));
                  onSuccess();
                  this.router.navigate(['/projects', 'challenge-builder', project._id, 'overview']);
                },
                onFailure: () => onFailure(new Error('Error stopping challenge')),
              })
            );
          },
        },
      });
      return;
    }
    this.editClick.emit();
  }

  onBackClick() {
    if (this.isEditView()) {
      this.exitEdit.emit();
    } else {
      this.router.navigate(['/projects']);
    }
  }

  selectProject(project: Project) {
    sessionStorage.setItem('ophStoredProjectId', project._id);
    this.projectSelected.emit(project);
  }

  onProjectAction(action: string) {
    const project = this.project();
    if (!project) return;
    if (action === 'edit') {
      if (project.isChallenge) {
        this.router.navigate(['projects', 'challenge-builder', project._id, 'overview']);
        return;
      }
      this.onOpenProjectDialog(project);
    }
    if (action === 'duplicate') {
      this.duplicate();
    }
    if (action === 'delete') {
      this.openDeleteConfirmation();
    }
  }

  onOpenProjectDialog(currentProject?: Project) {
    const dialog = this.dialog.open(ProjectsDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      ...PROJECTS_DIALOG_CONFIG,
      data: {project: currentProject},
    });
    dialog.afterClosed().subscribe(project => {
      if (project) {
        this.onActionSuccess(project);
        this.router.navigate(['projects', project._id]);
      }
    });
  }

  duplicate() {
    const project = this.project();
    if (!project) return;
    this.loading$.next({...this.loading$.value, projectAction: true});
    this.store.dispatch(
      new DuplicateProjectAction({
        id: project._id,
        onSuccess: p => this.onActionSuccess(p),
        onFailure: err => this.onActionFailure(err),
      })
    );
  }

  openDeleteConfirmation() {
    const project = this.project();
    if (!project) return;
    this.dialog.open(ChallengeWarningDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      backdropClass: 'oph-backdrop',
      panelClass: 'challenge-warning-dialog-panel',
      data: {
        type: project.isChallenge ? 'challenge' : 'project',
        action: 'delete',
        onConfirm: ({onSuccess, onFailure}) => {
          this.store.dispatch(
            new DeleteProjectAction({
              id: project._id,
              onSuccess: () => {
                this.store.dispatch(new GetProjectsAction({}));
                onSuccess();
              },
              onFailure: err => onFailure(err),
            })
          );
        },
      },
    });
  }

  onActionSuccess(project: Project) {
    this.store.dispatch(new GetProjectsAction({}));
    this.getProjectTrackers(project._id);
    if (project) {
      sessionStorage.setItem('ophStoredProjectId', project._id);
    }
    this.loading$.next({...this.loading$.value, projectAction: false});
  }

  getProjectTrackers = (projectId: string) => {
    this.store.dispatch(
      new GetAllTrackersAction({
        params: {projectId, pageSize: 1000},
      })
    );
  };

  onActionFailure(err: Error) {
    this.MessageService.add(err.message || 'There was an error with the Project.');
    this.loading$.next({...this.loading$.value, projectAction: false});
  }

  onAddSked() {
    const project = this.project();
    if (!project) return;
    this.dialog.open(ProjectsSkedSelectorDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      ...PROJECTS_ADD_SKED_DIALOG_CONFIG,
      data: {projectId: project._id},
    });
  }

  onAddUser() {
    const project = this.project();
    combineLatest([this.users$, this.usersMap$, this.teams$, this.teamsMap$])
      .pipe(take(1))
      .subscribe(([users, usersMap, teams, teamsMap]) => {
        this.dialog.open(ProjectsUsersTeamsSelectionDialogComponent, {
          autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
          ...PROJECTS_USERS_TEAMS_SELECTION_DIALOG_CONFIG,
          data: {projectId: project?._id, users, usersMap, teams, teamsMap},
        });
      });
  }

  onCreateNewChallenge() {
    this.router.navigate(['/projects', 'challenge-builder', 'new', 'goal']);
  }
}
